import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Copyright © 2025-2026 Cognizant Digital Business, Evolutionary AI.
 * All Rights Reserved.
 * Issued under the Academic Public License.
 *
 * You can be released from the terms, and requirements of the Academic Public
 * License by purchasing a commercial license.
 * Purchase of a commercial license is mandatory for any use of the
 * LMX Software in commercial settings.
 */
// END COPYRIGHT
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import { HelpIcon } from "../styles/HelpIcon";
import { LightTooltip } from "../styles/Tooltips";
// #region: Styled Components
const EmptyState = styled(Box)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    padding: 16,
    textAlign: "center",
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
}));
const LogsContainer = styled(Box)(({ theme }) => ({
    marginBottom: 24,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    maxHeight: "calc(100vh - 60px)",
    overflow: "hidden",
}));
const LogSection = styled(Box)(({ theme }) => ({
    marginBottom: 24,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: 12,
    backgroundColor: theme.palette.background.default,
    boxShadow: "0 6px 18px rgba(12,20,30,0.04)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    flex: 1,
    minHeight: 120,
}));
const LogTextArea = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    padding: 16,
    borderRadius: 4,
    fontFamily: "Monaco, Menlo, Consolas, monospace",
    fontSize: "0.8rem",
    lineHeight: 1.4,
    flex: 1,
    overflowY: "auto",
    whiteSpace: "pre-wrap",
    wordWrap: "break-word",
    "&::-webkit-scrollbar": {
        width: 8,
    },
    "&::-webkit-scrollbar-track": {
        background: theme.palette.background.paper,
    },
    "&::-webkit-scrollbar-thumb": {
        background: theme.palette.text.secondary,
        borderRadius: 4,
    },
    "&::-webkit-scrollbar-thumb:hover": {
        background: theme.palette.text.primary,
    },
}));
const LogTitle = styled("h3")(({ theme }) => ({
    color: theme.palette.text.primary,
    fontSize: "1.1rem",
    margin: "0 0 8px 0",
}));
const Title = styled("h2")(({ theme }) => ({
    color: theme.palette.text.primary,
    fontSize: "1.5rem",
    margin: "0 0 16px 0",
    display: "flex",
    alignItems: "center",
}));
// #endregion: Types
const Logs = ({ appState }) => {
    const generatorLog = appState?.generator_log || [];
    const evaluatorLog = appState?.evaluator_log || [];
    // TODO: Remove when we go to es2023
    // eslint-disable-next-line unicorn/no-array-reverse
    const reversedGeneratorLog = [...generatorLog].reverse();
    // eslint-disable-next-line unicorn/no-array-reverse
    const reversedEvaluatorLog = [...evaluatorLog].reverse();
    return (_jsxs(LogsContainer, { children: [_jsxs(Title, { children: ["Logs", _jsx(LightTooltip, { arrow: true, placement: "right", title: "This section displays the logs of the evaluator and generator LLMs. " +
                            "The generator LLM is used to generate ideas (so only AI-generated ideas are shown there). " +
                            "The evaluator LLM is used to evaluate ideas (so all ideas are shown there). " +
                            "Both logs show the prompts and responses for each interaction with the LLMs.", children: _jsx(HelpIcon, { "aria-label": "Logs help" }) })] }), _jsxs(LogSection, { children: [_jsx(LogTitle, { children: "Generator Log" }), generatorLog.length > 0 ? (_jsx(LogTextArea, { children: reversedGeneratorLog.join("\n\n") })) : (_jsx(EmptyState, { children: "No generator activity yet." }))] }), _jsxs(LogSection, { children: [_jsx(LogTitle, { children: "Evaluator Log" }), evaluatorLog.length > 0 ? (_jsx(LogTextArea, { children: reversedEvaluatorLog.join("\n\n") })) : (_jsx(EmptyState, { children: "No evaluator activity yet." }))] })] }));
};
export default Logs;
